// src/games/treasureQuest/QuestMap.js
//
// The quest map: a little world with a winding path, one stop per board,
// and the treasure chest at the end. The student's avatar (wearing their
// shop outfit) walks from stop to stop — CSS moves it, with a bounce while
// it walks. Six worlds (meadow, beach, snow, candy, jungle, space) take turns
// as the quest level climbs, so the map changes as well as the boards.

import React, { useMemo } from "react";
import AvatarWithOutfit from "../../components/avatar/AvatarWithOutfit";

const THEMES = {
  meadow: { sky: ["#bfe8ff", "#e8f7ff"], hill1: "#8fd16a", hill2: "#6cbf4f", path: "#f4d9a0", accent: "#ff7ac6", deco: "tree" },
  beach: { sky: ["#9fdcff", "#fff1c9"], hill1: "#ffe2a3", hill2: "#4cc3e6", path: "#fff7e0", accent: "#ff9a3c", deco: "palm" },
  snow: { sky: ["#cfe3ff", "#f4f8ff"], hill1: "#ffffff", hill2: "#dce9fb", path: "#b9d2f3", accent: "#3a86ff", deco: "pine" },
  candy: { sky: ["#ffd6ec", "#fff0f8"], hill1: "#ffb3d9", hill2: "#ff8cc6", path: "#fff", accent: "#8b5cf6", deco: "lolly" },
  jungle: { sky: ["#a8e6c8", "#e9fff4"], hill1: "#3fbf7f", hill2: "#2a9d63", path: "#e8c98e", accent: "#ffb703", deco: "palm" },
  space: { sky: ["#1c1446", "#3b2a86"], hill1: "#6c4ce6", hill2: "#4a2fb8", path: "#ffd23f", accent: "#2ec4b6", deco: "planet" },
};

/** Stops along an S-shaped path, in % of the map (x 0–100, y 0–100). */
export const stopsFor = (boards) => {
  const n = boards + 2; // start, boards…, chest
  return Array.from({ length: n }, (_, i) => {
    const k = i / (n - 1);
    return { x: 9 + k * 80, y: 74 - k * 46 + Math.sin(k * Math.PI * 2.2) * 15 };
  });
};

const Deco = ({ kind, x, y, s = 1, accent }) => {
  const t = `translate(${x} ${y}) scale(${s})`;
  if (kind === "palm")
    return (
      <g transform={t}>
        <path d="M0 0 Q2 -14 -1 -28" stroke="#8a5a2b" strokeWidth="2.6" fill="none" />
        <path d="M-1 -28 Q-12 -32 -16 -24 M-1 -28 Q10 -34 15 -25 M-1 -28 Q-6 -38 -14 -36 M-1 -28 Q6 -40 13 -37" stroke="#7ed957" strokeWidth="3.6" fill="none" strokeLinecap="round" />
        <path d="M-1 -28 Q-12 -32 -16 -24 M-1 -28 Q10 -34 15 -25" stroke="#2f7d32" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      </g>
    );
  if (kind === "pine")
    return (
      <g transform={t}>
        <path d="M0 -30 L9 -14 L5 -14 L12 -2 L-12 -2 L-5 -14 L-9 -14 Z" fill="#3e8a6a" />
        <path d="M0 -30 L5 -21 L-5 -21 Z" fill="#fff" />
        <rect x="-1.5" y="-2" width="3" height="4" fill="#8a5a2b" />
      </g>
    );
  if (kind === "lolly")
    return (
      <g transform={t}>
        <rect x="-1" y="-14" width="2" height="16" fill="#fff" />
        <circle cx="0" cy="-20" r="8" fill={accent} />
        <path d="M-5 -20 A5 5 0 0 1 5 -20" stroke="#fff" strokeWidth="2" fill="none" />
      </g>
    );
  if (kind === "planet")
    return (
      <g transform={t}>
        <circle cx="0" cy="-14" r="7" fill={accent} />
        <ellipse cx="0" cy="-14" rx="12" ry="3" fill="none" stroke="#ffd23f" strokeWidth="1.6" />
      </g>
    );
  return (
    <g transform={t}>
      <rect x="-1.5" y="-8" width="3" height="10" fill="#8a5a2b" />
      <circle cx="0" cy="-14" r="9" fill="#4caf50" />
      <circle cx="-3" cy="-17" r="3" fill="#7fd36b" />
    </g>
  );
};

const Chest = ({ open }) => (
  <svg viewBox="0 0 100 90" className={`tq-chest ${open ? "is-open" : ""}`} aria-hidden="true">
    {open && <ellipse cx="50" cy="40" rx="46" ry="30" fill="#fff6b8" opacity="0.8" className="tq-chest-glow" />}
    <rect x="12" y="44" width="76" height="38" rx="6" fill="#b5651d" stroke="#2a2130" strokeWidth="4" />
    <rect x="12" y="56" width="76" height="6" fill="#ffd23f" stroke="#2a2130" strokeWidth="2" />
    {open ? (
      <>
        <path d="M14 44 Q50 34 86 44 L80 42 Q50 30 20 42 Z" fill="#ffd23f" />
        <circle cx="38" cy="40" r="7" fill="#ffd23f" stroke="#b07a00" strokeWidth="2" />
        <circle cx="56" cy="38" r="7" fill="#ffd23f" stroke="#b07a00" strokeWidth="2" />
        <circle cx="47" cy="33" r="6" fill="#ff4d6d" stroke="#2a2130" strokeWidth="2" />
        <path d="M14 42 L20 10 Q50 0 80 10 L86 42 Z" fill="#c97a2e" stroke="#2a2130" strokeWidth="4" transform="translate(0 -12) skewX(-6)" />
      </>
    ) : (
      <path d="M12 46 Q12 20 50 18 Q88 20 88 46 Z" fill="#c97a2e" stroke="#2a2130" strokeWidth="4" />
    )}
    <rect x="44" y="50" width="12" height="14" rx="3" fill="#ffd23f" stroke="#2a2130" strokeWidth="3" />
  </svg>
);

/**
 * Props: theme, boards (count), at (stop index the avatar stands on),
 * walking (bool), results [{ stars }], chestOpen, look, avatarUrl, outfit,
 * levelFrom (first level number), labels (optional stop labels instead of
 * level numbers — the daily trail uses them), children (buttons shown on the map)
 */
const QuestMap = ({ theme = "meadow", boards, at, walking, results = [], chestOpen, look = null, avatarUrl, outfit, levelFrom, labels = null, children }) => {
  const T = THEMES[theme] || THEMES.meadow;
  const stops = useMemo(() => stopsFor(boards), [boards]);
  const pathD = useMemo(() => {
    const p = stops.map((s) => [s.x * 10, s.y * 6.25]);
    let d = `M${p[0][0]} ${p[0][1]}`;
    for (let i = 1; i < p.length; i += 1) {
      const [x0, y0] = p[i - 1];
      const [x1, y1] = p[i];
      const mx = (x0 + x1) / 2;
      d += ` C${mx} ${y0} ${mx} ${y1} ${x1} ${y1}`;
    }
    return d;
  }, [stops]);
  const me = stops[Math.min(at, stops.length - 1)];

  return (
    <div className={`tq-map tq-theme-${theme}`}>
      <svg className="tq-map-scene" viewBox="0 0 1000 625" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <linearGradient id={`tq-sky-${theme}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={T.sky[0]} />
            <stop offset="1" stopColor={T.sky[1]} />
          </linearGradient>
        </defs>
        <rect width="1000" height="625" fill={`url(#tq-sky-${theme})`} />
        {theme === "space" &&
          Array.from({ length: 40 }, (_, i) => (
            <circle key={i} cx={(i * 263) % 1000} cy={(i * 97) % 300} r={(i % 3) + 1} fill="#fff" opacity="0.8" className="tq-twinkle" style={{ animationDelay: `${(i % 7) * 0.4}s` }} />
          ))}
        {theme !== "space" && (
          <g className="tq-clouds">
            <ellipse cx="160" cy="90" rx="70" ry="22" fill="#fff" opacity="0.9" />
            <ellipse cx="200" cy="78" rx="40" ry="20" fill="#fff" opacity="0.9" />
            <ellipse cx="720" cy="60" rx="80" ry="22" fill="#fff" opacity="0.85" />
            <ellipse cx="770" cy="48" rx="44" ry="20" fill="#fff" opacity="0.85" />
          </g>
        )}
        <path d="M0 330 Q250 240 520 320 T1000 290 V625 H0 Z" fill={T.hill2} />
        <path d="M0 420 Q300 330 600 410 T1000 380 V625 H0 Z" fill={T.hill1} />
        {[[90, 470, 1.6], [300, 560, 1.3], [470, 330, 1.1], [640, 580, 1.5], [880, 450, 1.4], [960, 560, 1.2], [200, 360, 1], [780, 330, 1]].map(([x, y, s], i) => (
          <Deco key={i} kind={T.deco} x={x} y={y} s={s * 1.6} accent={T.accent} />
        ))}
        {/* the path */}
        <path d={pathD} fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="30" strokeLinecap="round" transform="translate(0 6)" />
        <path d={pathD} fill="none" stroke={T.path} strokeWidth="26" strokeLinecap="round" />
        <path d={pathD} fill="none" stroke="#fff" strokeWidth="4" strokeDasharray="2 18" strokeLinecap="round" opacity="0.8" />
      </svg>

      {/* stops */}
      {stops.map((s, i) => {
        const isStart = i === 0;
        const isChest = i === stops.length - 1;
        const done = !isStart && !isChest && results[i - 1];
        const next = i === at + 1 && !walking;
        if (isChest) {
          return (
            <span key="chest" className="tq-stop tq-stop-chest" style={{ left: `${s.x}%`, top: `${s.y}%` }}>
              <Chest open={chestOpen} />
            </span>
          );
        }
        return (
          <span
            key={i}
            className={`tq-stop ${isStart ? "is-start" : ""} ${done ? (done.won ? "is-won" : "is-lost") : ""} ${next ? "is-next" : ""}`}
            style={{ left: `${s.x}%`, top: `${s.y}%` }}
          >
            {isStart ? "🏁" : labels ? labels[i - 1] : levelFrom + i - 1}
            {done?.won && done.stars != null && (
              <span className="tq-stop-stars">
                {[1, 2, 3].map((n) => (
                  <i key={n} className={n <= done.stars ? "is-on" : ""}>
                    ★
                  </i>
                ))}
              </span>
            )}
          </span>
        );
      })}

      {/* the avatar */}
      <span className={`tq-walker ${walking ? "is-walking" : ""}`} style={{ left: `${me.x}%`, top: `${me.y}%` }}>
        <span className="tq-walker-body">
          <AvatarWithOutfit look={look} avatarUrl={avatarUrl} outfit={outfit} size={64} />
        </span>
        <span className="tq-walker-shadow" />
      </span>

      <div className="tq-map-ui">{children}</div>
    </div>
  );
};

export default QuestMap;
